import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Play } from 'lucide-react';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { MonthlyListenersData } from '../../services/api/artist-analytics.service';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface DateRange {
  from: Date;
  to: Date;
}

interface YouTubeMusicViewsMetricProps {
  artistId?: string;
  dateRange?: DateRange;
  className?: string;
  refreshKey?: number;
}

const YouTubeMusicViewsMetric: React.FC<YouTubeMusicViewsMetricProps> = ({
  artistId,
  dateRange,
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<MonthlyListenersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate months from date range for API call
  const getMonthsFromDateRange = (): number => {
    if (!dateRange || !dateRange.from || !dateRange.to) {
      return 1; // Default to 1 month for 30 days
    }

    const diffTime = Math.abs(dateRange.to.getTime() - dateRange.from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Convert days to approximate months, with minimum of 1
    return Math.max(1, Math.ceil(diffDays / 30));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const months = getMonthsFromDateRange();
        const response = await artistAnalyticsService.getYouTubeViewsData(artistId, months);
        setData(response.data);
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, dateRange, refreshKey]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000000) {
      // Billions - show with 1 decimal place if under 100B, no decimals if over
      const billions = num / 1000000000;
      if (billions >= 100) {
        return `${Math.round(billions)}B`;
      }
      return `${billions.toFixed(1)}B`;
    } else if (num >= 1000000) {
      // Millions - show with 1 decimal place if under 100M, no decimals if over
      const millions = num / 1000000;
      if (millions >= 100) {
        return `${Math.round(millions)}M`;
      }
      return `${millions.toFixed(1)}M`;
    } else if (num >= 1000) {
      // Thousands - show with 1 decimal place if under 100K, no decimals if over
      const thousands = num / 1000;
      if (thousands >= 100) {
        return `${Math.round(thousands)}K`;
      }
      return `${thousands.toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const calculateGrowthPercentage = (): { percentage: number; isPositive: boolean } => {
    if (!data || !data.table || data.table.length === 0 || !data.chart.xAxisData || data.chart.xAxisData.length < 2) {
      return { percentage: 0, isPositive: true };
    }

    // Find YouTube Music data in the table
    const youtubeData = data.table.find(row => row.platform === 'YouTube Music');
    if (!youtubeData) {
      return { percentage: 0, isPositive: true };
    }

    const months = data.chart.xAxisData;
    const firstMonth = youtubeData.monthlyData[months[0]] || 0;
    const lastMonth = youtubeData.monthlyData[months[months.length - 1]] || 0;
    
    if (firstMonth === 0) {
      return { percentage: 0, isPositive: true };
    }

    const percentage = ((lastMonth - firstMonth) / firstMonth) * 100;
    return { 
      percentage: Math.abs(percentage), 
      isPositive: percentage >= 0 
    };
  };

  if (loading) {
    return (
      <div className="bg-[#1C1C1C] rounded-lg p-4 flex-1">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-sm-nova font-medium text-white uppercase">TOTAL VIEWS</h3>
            <div className="text-lg font-semibold text-gray-400 mt-1 animate-pulse">Loading...</div>
          </div>
          <div className="text-red-500">
            <Play size={48} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-[#1C1C1C] rounded-lg p-4 flex-1">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-sm-nova font-medium text-white uppercase">TOTAL VIEWS</h3>
            <p className="text-xs text-red-400 mt-1">{CHART_NO_DATA_FOR_ARTIST}</p>
          </div>
          <div className="text-red-500">
            <Play size={48} />
          </div>
        </div>
      </div>
    );
  }

  const totalViews = data.totalValue || 0;
  const displayTotal = formatNumber(totalViews);
  const growth = calculateGrowthPercentage();

  return (
    <div className={`bg-[#1C1C1C] rounded-lg p-4 flex-1 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="text-sm-nova font-medium text-white uppercase">TOTAL VIEWS</h3>
          <p className="text-lg font-semibold text-white mt-1">
            {displayTotal}
          </p>
          <div className={`flex items-center gap-1 mt-2 ${growth.isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {growth.isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span className="text-sm-nova">
              {growth.isPositive ? '+' : '-'}{growth.percentage.toFixed(1)}%
            </span>
          </div>
        </div>
        <div className="text-red-500">
          <Play size={48} fill="currentColor" />
        </div>
      </div>
    </div>
  );
};

export default YouTubeMusicViewsMetric;
